// shared-lib/books-price-book/offering.js
//
// What a contractor OFFERS: whether a vendor cabinet line, one of its door
// designs, one of its colours, or one unit in one colour is something the
// company sells. Decided 2026-10-09 (option C of scope-of-work's question
// one): an `offered` flag on the price book's own rows.
//
//   cabinet-line--<family>                    the whole line
//   cabinet-line--<family>--door--<designId>  one door design (a series)
//   cabinet-line--<family>--colour--<code>    one colour
//   cabinet-sku--<family>--<SKU>--<code>      one unit in one colour — the
//                                             SAME row its price lives on
//
// The first three are small PRICE-FREE rows; a unit's flag rides beside its
// price. The most specific flag said wins (unit, colour, door, line); where
// nothing is said, `offeredByDefault` answers. `offered: null` (or no field)
// is "not said", never "not offered".
//
// Offered never changes a price, and a row that only says what is offered is
// not a priced item — every count of priced entries skips it through
// `isOfferedOnly`. Pure.

const { SHOWER_SYSTEM_KEY_PREFIX } = require('../shower-systems');

const LINE_PREFIX = 'cabinet-line--';

const cabinetLineKey = (familyId) => `${LINE_PREFIX}${familyId}`;
/**
 * How a finish is named in a `--colour--` key: a vendor paint by its code
 * (Venus's `HF`, the code its prices are keyed by), any other paint by its
 * id, a wood (a texture id in the family's `woods`) by that id.
 */
const finishCode = (finish) =>
  typeof finish === 'string' ? finish : (finish?.code ?? finish?.id);
const cabinetColourKey = (familyId, code) =>
  `${cabinetLineKey(familyId)}--colour--${code}`;
const cabinetDoorKey = (familyId, designId) =>
  `${cabinetLineKey(familyId)}--door--${designId}`;

/**
 * The flag a row says: true / false, or null when it says nothing. A row is a
 * book entry (`{ offered, … }`) or, in the offering MAP, the flag itself — so
 * every helper here reads either.
 */
const offeredFlag = (entry) => {
  if (typeof entry === 'boolean') return entry;
  return typeof entry?.offered === 'boolean' ? entry.offered : null;
};

/**
 * The company's offering as it travels — to the Design Center, a share, a
 * sandbox: `{ key: boolean }` for every row that says a flag. No figures.
 */
const offeringMap = (entries) =>
  Object.fromEntries(
    Object.entries(entries || {})
      .map(([key, entry]) => [key, offeredFlag(entry)])
      .filter(([, flag]) => flag !== null)
  );

/**
 * The answer where nothing is said — scope-of-work's question three, and the
 * ONE place it lives (the `texture--` / `model--` readers share it). The
 * user chose option A (2026-10-09): a book with no row saying
 * `offered: true` offers everything (today's behaviour), and once one does,
 * the company has said what it offers and an unflagged item is not offered —
 * four Venus colours ticked means exactly those four.
 *
 * Returns `(ignoring) => boolean`, worked out once for a book. `ignoring`
 * leaves one row's flag out: what a switch inherits were its own flag
 * cleared. Without it, clearing the book's only `true` would flip the
 * default and the switch would spring back.
 */
function offeringDefaults(entries) {
  const book = entries || {};
  // A shower system's row is left out: a system is offered only where its
  // own row says so, never by default (2026-10-28, `shower-systems`), so a
  // company ticking one has not said what else it sells.
  const said = Object.keys(book).filter(
    (k) =>
      !k.startsWith(SHOWER_SYSTEM_KEY_PREFIX) && offeredFlag(book[k]) === true
  );
  return (ignoring) =>
    said.length === 0 || (said.length === 1 && said[0] === ignoring);
}

const offeredByDefault = (entries, ignoring) =>
  offeringDefaults(entries)(ignoring);

/**
 * Whether one unit in one colour is offered, and which row said so —
 * `from` is 'sku' | 'colour' | 'door' | 'line' | 'default'. Any of
 * `skuKey`, `colourCode`, `designId` may be omitted to ask about something
 * broader (a colour, a door design, the line). `byDefault` is the default
 * already worked out (`offeringDefaults`), for a caller asking many times.
 */
function cabinetOffered({
  entries,
  familyId,
  colourCode,
  designId,
  skuKey,
  byDefault,
}) {
  const book = entries || {};
  const steps = [
    ['sku', skuKey],
    ['colour', colourCode != null && cabinetColourKey(familyId, colourCode)],
    ['door', designId != null && cabinetDoorKey(familyId, designId)],
    ['line', cabinetLineKey(familyId)],
  ];
  for (const [from, key] of steps) {
    const flag = key ? offeredFlag(book[key]) : null;
    if (flag !== null) return { offered: flag, from };
  }
  return { offered: byDefault ?? offeredByDefault(book), from: 'default' };
}

/**
 * Whether one catalog item — a tile, slab, fixture, keyed `texture--<id>` /
 * `model--<id>` — is offered: its own row says, or the default does. No
 * chain: a flat catalog item has no line above it.
 */
function itemOffered({ entries, key, byDefault }) {
  const flag = offeredFlag((entries || {})[key]);
  if (flag !== null) return { offered: flag, from: 'item' };
  return { offered: byDefault ?? offeredByDefault(entries), from: 'default' };
}

/**
 * The flag a switch writes when set to `wantOffered`: nothing (null) where
 * that is what it would inherit anyway, so the book holds only the
 * exceptions and a switch put back follows its parent again.
 */
const nextOfferedFlag = (wantOffered, inherited) =>
  wantOffered === inherited ? null : wantOffered;

/** A catalog item's price book key: `texture--<id>` or `model--<id>`. */
const catalogItemKey = (kind, id) => `${kind}--${id}`;

/**
 * `items` (tiles, slabs, fixtures — `kind` 'texture' or 'model') filtered to
 * what `company` offers, each by its own row (`itemOffered`). No flags: the
 * very list given.
 */
function offeredCatalog(items, company, kind) {
  if (!company || Object.keys(company).length === 0) return items;
  const byDefault = offeringDefaults(company)();
  return (items || []).filter(
    (item) =>
      itemOffered({
        entries: company,
        key: catalogItemKey(kind, item.id),
        byDefault,
      }).offered
  );
}

/** Whether a stored pick (`id` of `kind`) is one `company` does not offer. */
const itemNotOffered = (id, company, kind) =>
  !itemOffered({ entries: company || {}, key: catalogItemKey(kind, id) })
    .offered;

const hasNumber = (v) => typeof v === 'number' && Number.isFinite(v);

/** True for a row with a cost, a price or hours — a priced item. */
const isPricedEntry = (entry) =>
  !!entry &&
  (hasNumber(entry.cost) || hasNumber(entry.price) || hasNumber(entry.hours));

/**
 * True for a row that says only what is offered — a flag, no cost, price or
 * hours. Such a row is not a priced item and is never counted as one.
 */
function isOfferedOnly(entry) {
  return (
    offeredFlag(entry) !== null &&
    !hasNumber(entry.cost) &&
    !hasNumber(entry.price) &&
    !hasNumber(entry.hours)
  );
}

module.exports = {
  offeringDefaults,
  offeredByDefault,
  cabinetLineKey,
  cabinetColourKey,
  cabinetDoorKey,
  finishCode,
  offeredFlag,
  offeringMap,
  cabinetOffered,
  itemOffered,
  catalogItemKey,
  offeredCatalog,
  itemNotOffered,
  nextOfferedFlag,
  isOfferedOnly,
  isPricedEntry,
};
